import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { Gtag } from 'angular-gtag';
import { combineLatest, Observable, withLatestFrom } from 'rxjs';
import { AddressOrders, AddressOrdersFormItem, AddressType, Order, PatientAddress } from 'src/app/core/models';
import {
  ClearPreviousShippingAddressOrders,
  UpdateOrdersToDefaultShipping,
} from 'src/app/core/store/addresses/addresses.actions';
import { AddressesState } from 'src/app/core/store/addresses/addresses.state';
import { ApiStoreData } from 'src/app/core/store/utils';
import { asFormControl } from 'src/app/core/utils/common';
import { FormatAddressPipe } from 'src/app/core/utils/pipes';
import { validateAllFormFields } from 'src/app/core/utils/validators';

@Component({
  selector: 'app-address-orders-form',
  templateUrl: './address-orders-form.component.html',
  styleUrls: ['./address-orders-form.component.scss'],
})
export class AddressOrdersFormComponent implements OnInit, OnChanges {
  @Input() visible!: boolean;
  @Input() excludeOrderId?: number;
  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() submitForm: EventEmitter<void> = new EventEmitter<void>();

  @Select(AddressesState.patientAddressesState) patientAddressesState$!: Observable<ApiStoreData<PatientAddress[]>>;
  @Select(AddressesState.addressOrdersState) addressOrdersState$!: Observable<ApiStoreData<AddressOrders | null>>;
  @Select(AddressesState.updateDefaultShippingOrdersState)
  updateDefaultShippingOrdersState$!: Observable<ApiStoreData<null>>;

  addressOrdersForm: FormGroup = new FormGroup({
    orderUpdates: new FormArray([]),
  });
  orders: Order[] = [];
  formatAddressPipe: FormatAddressPipe = new FormatAddressPipe();
  currentAddressStr: string | null = null;
  newAddressStr: string | null = null;

  showError: boolean = false;

  asFormControl = asFormControl;

  constructor(private store: Store, private gtag: Gtag) {}

  ngOnInit() {
    this.gtag.event('address_changes_modal_shown', {
      event_category: 'engagement',
      non_interaction: true,
    });

    combineLatest([this.patientAddressesState$, this.addressOrdersState$]).subscribe(
      ([patientAddressesState, addressOrdersState]) => {
        this.orders = addressOrdersState.data?.orders || [];

        this.currentAddressStr = this.formatAddressPipe.transform(
          patientAddressesState.data?.find((a) => a.id === addressOrdersState.data?.addressId),
          { singleLine: true }
        );

        this.newAddressStr = this.formatAddressPipe.transform(
          patientAddressesState.data?.find((a) => a.types.includes(AddressType.Shipping)),
          { singleLine: true }
        );

        this.updateForm();
      }
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['excludeOrderId']) {
      this.updateForm();
    }
  }

  updateForm() {
    if (this.orders.length) {
      this.addressOrdersForm.setControl(
        'orderUpdates',
        new FormArray(
          this.orders
            .filter((o) => o.id !== this.excludeOrderId)
            .map(
              (order) =>
                new FormGroup({
                  orderId: new FormControl(order.id),
                  updateAddress: new FormControl(true),
                })
            )
        )
      );
    } else {
      this.resetForm();
    }
  }

  getOrderControls() {
    return (this.addressOrdersForm.get('orderUpdates')! as FormArray).controls;
  }

  onVisibleChange(value: boolean) {
    this.visibleChange.emit(value);
  }

  hideModal() {
    this.visible = false;
    this.onVisibleChange(this.visible);
  }

  showModal() {
    this.visible = true;
    this.onVisibleChange(this.visible);
  }

  resetForm() {
    this.addressOrdersForm.setControl('orderUpdates', new FormArray([]));
    this.currentAddressStr = null;
    this.newAddressStr = null;
    this.showError = false;
  }

  onCancel() {
    this.hideModal();
    this.gtag.event('cancel_address_changes_modal_no_changes', {
      event_category: 'engagement',
    });
    this.store.dispatch(new ClearPreviousShippingAddressOrders());
    this.resetForm();
  }

  getOrder(orderId: number) {
    return this.orders.find((o) => o.id === orderId);
  }

  onSubmit() {
    if (!this.addressOrdersForm.valid) {
      validateAllFormFields(this.addressOrdersForm);
      return;
    }

    if (!this.addressOrdersForm.value.orderUpdates.some((ou: AddressOrdersFormItem) => ou.updateAddress)) {
      this.onCancel();
      return;
    }

    this.gtag.event('submit_address_changes_modal_changes', {
      event_category: 'engagement',
    });

    this.store
      .dispatch(new UpdateOrdersToDefaultShipping(this.addressOrdersForm.value))
      .pipe(withLatestFrom(this.updateDefaultShippingOrdersState$))
      .subscribe(([_, updateDefaultShippingOrdersState]) => {
        if (!updateDefaultShippingOrdersState.error) {
          this.submitForm.emit();
          this.hideModal();
          this.store.dispatch(new ClearPreviousShippingAddressOrders());
          this.resetForm();
        } else {
          this.showError = true;
        }
      });
  }
}
